// libs
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// types
import { BreadCrumbs } from 'shared/types/breadCrumbs';

// constants
import { DEFAULT_CURRENCY_CODE } from 'shared/constants';

// helpers
import { getDomainConfig } from 'shared/domainConfig/helpers';

/**
 * Generate review data (AggregateRating + Review) to nest inside Product schema
 * Only includes rendered reviews (top_reviews shown on initial load) as per PRD
 */
export const getReviewData = (review: Review.IReviewSummary) => {
  const data: Record<string, unknown> = {};
  if (isEmpty(review) || !review?.show_ratings || review.review_count < 1) {
    return data;
  }

  // AggregateRating
  if (review.star_rating && review.star_rating_count) {
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: review.star_rating,
      ratingCount: review.star_rating_count,
      reviewCount: review.review_count,
      bestRating: '5',
    };
  }

  // Review — only rendered top reviews
  const topReviews = get(review, 'top_reviews', []);
  if (!isEmpty(topReviews)) {
    const reviewSchemas = topReviews.map((reviewItem: Review.ITopReview) => ({
      '@type': 'Review',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: reviewItem.rating,
        bestRating: '5',
      },
      author: {
        '@type': 'Person',
        name: reviewItem.nickname || reviewItem.name || 'Anonymous',
      },
      datePublished: reviewItem.created_at || reviewItem.createdOn,
      reviewBody: reviewItem.detail || reviewItem.description,
    }));

    if (reviewSchemas.length > 0) {
      data.review = reviewSchemas;
    }
  }

  return data;
};

/**
 * Generate BreadcrumbList schema for PDP
 * Returns separate schema as per PRD requirements
 */
export const getBreadcrumbListSchema = (breadCrumbs: BreadCrumbs) => {
  if (!breadCrumbs?.length) {
    return null;
  }

  const { SHARE } = getDomainConfig();

  const breadCrumbList = breadCrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb?.key,
    item: `${SHARE.CANONICAL_HOMEPAGE_URL}${crumb?.value}`,
  }));

  return {
    '@context': 'https://schema.org/',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbList,
  };
};

/**
 * Generate Product JSON-LD with AggregateRating + Review nested inside
 * BreadcrumbList is kept as a separate schema
 */
export const getJSONLD = ({
  metaInfo,
  title,
  subTitle,
  SKU,
  isOutOfStock,
  price,
  hasDiscount,
  discountPrice,
  review,
}: {
  metaInfo: ProductDetail.IMetaData;
  title: string;
  subTitle: string;
  SKU: string;
  isOutOfStock: boolean;
  price: number;
  hasDiscount: boolean;
  discountPrice: number;
  review: Review.IReviewSummary;
}) => {
  const brand = title ? { '@type': 'Brand', name: title } : undefined;

  return JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: `${title} ${subTitle}`,
    description: metaInfo.description,
    image: metaInfo.imgUrl,
    sku: SKU,
    mpn: SKU,
    brand,
    offers: {
      '@type': 'Offer',
      url: metaInfo.productUrl,
      priceCurrency: DEFAULT_CURRENCY_CODE,
      price: hasDiscount ? discountPrice : price,
      itemCondition: 'https://schema.org/NewCondition',
      availability: `https://schema.org/${isOutOfStock ? 'OutOfStock' : 'InStock'}`,
    },
    ...getReviewData(review),
  });
};
